
import { create } from 'zustand';
import { prospectiveCaptureBinding, reconcileCaptureBinding, captureSide } from './prospectiveCaptureBinding';
import { exportStageToBlob } from './utils/exportSurface';
import { assessCardDesign, renderCardToBlob } from '../../rendering/konvaCardRenderer';
import { loadFonts as loadEveryIdFonts } from '../../services/fontService';
import { alignObjects, distributeObjects, fitObjectsWithinCard } from './designerLayout';
import { getStageDimensions } from './cardSizes';
const SURFACE_KEYS = ['front', 'frontSilver', 'back', 'backSilver'];

const deepClone = (v) => JSON.parse(JSON.stringify(v ?? []));

const generateId = () =>
  `obj-${Date.now()}-${Math.floor(Math.random() * 10000)}`;

  const cloneObjectForSilver = (obj) => ({
  ...structuredClone(obj),   // or JSON.parse(JSON.stringify(obj))
  id: generateId(),           // MUST be new
});

const stripRuntimeProps = (obj) => {
  const { _qrImage, _qrCacheKey, ...rest } = obj;
  return rest;
};



const splitBackground = (objects) => {
  if (objects.length > 0 && objects[0]?.isBackground) {
    return {
      bg: objects[0],
      rest: objects.slice(1)
    };
  }
  return {
    bg: null,
    rest: objects
  };
};

const getDefaultSurfaces = () => ({
  front: [],
  frontSilver: [],
  back: [],
  backSilver: [],
});

const normalizeSurfaceKey = (k) =>
  SURFACE_KEYS.includes(k) ? k : 'front';

const isBackground = (obj) => obj?.isBackground === true;

const createCardSettingSnapshot = (state, surfaces = state.surfaces) => ({
  kind: 'card-setting',
  cardSettings: { ...state.cardSettings },
  surfaces: deepClone(surfaces),
  activeSurface: state.activeSurface,
  objects: deepClone(state.objects),
  settingTransitionWarnings: deepClone(state.settingTransitionWarnings || [])
});

const isCardSettingSnapshot = entry => entry?.kind === 'card-setting';


const useDesignerStore = create((set, get) => ({

  exportCurrentSurface: async (format = 'png') => {
  const state = get();
  const { stageRef, scale, cardWidth, cardHeight } = state;

  if (!stageRef?.current) return null;

  state.flushTransforms?.();
  const surfaces = {
    ...state.surfaces,
    [state.activeSurface]: state.objects.map(stripRuntimeProps)
  };
  const cardDesign = {
    cardSettings: state.cardSettings,
    objectsFront: surfaces.front,
    objectsFrontSilver: surfaces.frontSilver,
    objectsBack: surfaces.back,
    objectsBackSilver: surfaces.backSilver
  };
  const assessment = assessCardDesign(cardDesign, { surface: state.activeSurface });
  const hasUnresolvedPhotoPlaceholder = assessment.objects.some(object =>
    object?.type === 'photo' &&
    !(object.originalSrc || object.src || object.imagePlaceholder?.previewSrc));

  if (format === 'png' && assessment.supported && !hasUnresolvedPhotoPlaceholder) {
    const result = await renderCardToBlob({
      cardDesign,
      surface: state.activeSurface,
      loadFonts: loadEveryIdFonts
    });
    return result.blob;
  }

  return await exportStageToBlob({
    stage: stageRef.current,
    scale,
    cardWidth,
    cardHeight,
    format
  });
},
  //
  // exportCurrentSurface: null,
  // setExporter: (fn) => set({ exportCurrentSurface: fn }),

  fontOptions: [],
  fontsReady: false,
  loadFonts: async () => {
    const families = await loadEveryIdFonts();
    set({ fontOptions: families });
    set({ fontsReady: true });
  },
  stageRef: null,
  setStageRef: (ref) => set({ stageRef: ref }),

  objects: [],
  surfaces: getDefaultSurfaces(),
  activeSurface: 'front',

  selectedIds: [],
  history: [],
  redoStack: [],
  settingTransitionWarnings: [],
  clipboard: [],
  gridSize:30,
  showGrid: true,
  isSnapToGridEnabled: true,
  isAspectRatioLocked: false,

  selectedObjectId: null,
  setSelectedObjectId: (id) => set({ selectedObjectId: id }),
  flushTransforms: null,
  setFlushTransforms: (fn) => set({ flushTransforms: fn }),
  // Design metadata
  cardId: null,
  capturePhotoBinding: null,
  newCaptureInputDraft: false,
  createdCaptureInputs: [],
  captureInputSelection: null,
  setCaptureInputSelection: key => set({captureInputSelection:key,isDirty:true}),
  validateCapturePhotoInput: () => prospectiveCaptureBinding(get()),
  addCapturedPhotoInput: obj => {
    const state=get();
    state.addObject(obj);
    if(state.newCaptureInputDraft && !state.cardId)
      set({createdCaptureInputs:[...state.createdCaptureInputs,{side:captureSide(state.activeSurface),elementId:obj.id}]});
  },
  orgId: '',
  name: '',
  description: '',
  // setName: (name) => set({ name }),
  // setOrgId: (orgId) => set({ orgId }),
  // setDescription: (description) => set({ description }),
  // datasetName: '',
  cardSettings: {
    size: 'cr80',
    format: 'landscape',
    sides: 'Simplex',
    display: 'Side 1'
  },
  stageKey: 0,
  selectedTemplateId: null,
  setSelectedTemplateId: (templateId) => set({ selectedTemplateId: templateId }),


  // Core state setters
  setSelectedIds: (ids) => set({ selectedIds: ids }),
  toggleSelectedId: (id) => set((state) => ({
    selectedIds: state.selectedIds.includes(id)
      ? state.selectedIds.filter(item => item !== id)
      : [...state.selectedIds, id]
  })),
  clearSelection: () => set({ selectedIds: [] }),
  selectAll: () => set((state) => ({
    selectedIds: state.objects.map(obj => obj.id)
  })),
  setName: (name) => set({ name }),
  setDescription: (description) => set({ description }),
  setCardId: (id) => set({ cardId: id }),
  setOrgId: (orgId) => set({ orgId }),
  datasetId: '',
  datasetName: '',
  setDataSetId: (datasetId) => set({ datasetId }),
  setDataSetName: (datasetName) => set({ datasetName }),
  setCardSettings: (settings) => set({ cardSettings: settings }),

  // Grid
  toggleSnapToGrid: () => set((state) => ({
    isSnapToGridEnabled: !state.isSnapToGridEnabled
  })),
  setGridSize: (size) => set({ gridSize: size }),
  toggleGrid: () => set(state => ({
    showGrid: !state.showGrid
  })),

  // Aspect Ratio
  setAspectRatioLock: (isLocked) => set({ isAspectRatioLocked: isLocked }),

  resizeBackgroundsToCard: (cw, ch) => {
    set(state => {
      // ⬅️ ALWAYS read from the incoming state snapshot
      // const cw = state.cardWidth;
      // const ch = state.cardHeight;

      // Guard: never write invalid geometry
      if (!cw || !ch) {
        return {};
      }

      const resize = objs =>
        objs.map(o =>
          o.isBackground
            ? {
                ...o,
                x: 0,
                y: 0,
                width: cw,
                height: ch,
              }
            : o
        );

      return {
        objects: resize(state.objects),
        surfaces: Object.fromEntries(
          Object.entries(state.surfaces).map(([k, v]) => [
            k,
            resize(v),
          ])
        ),
      };
    });
  },



  // Object actions
  // IMPORTANT:
// objects is the live source of truth.
// NEVER rebuild objects from surfaces here.
addObject: (obj) => {
  set(state => {
    const normalized = {
      density: 1.0,
      ...obj
    };

    const hasBackground = state.objects.some(o => o.isBackground);

    let nextObjects;

    if (normalized.isBackground) {
      // 🔒 remove any existing background
      const nonBackgrounds = state.objects.filter(o => !o.isBackground);
      nextObjects = [normalized, ...nonBackgrounds];
    } else if (hasBackground) {
      // insert after background
      nextObjects = [
        state.objects[0],
        ...state.objects.slice(1),
        normalized
      ];
    } else {
      nextObjects = [...state.objects, normalized];
    }

    return {
      objects: nextObjects,
      selectedIds: [normalized.id],
      isDirty: true
    };
  });
},



  deleteObject: (id) =>
  set((state) => ({
    history: [...state.history, state.objects],
    objects: state.objects.filter(obj => obj.id !== id),
    selectedIds: state.selectedIds.filter(selId => selId !== id),
    redoStack: []
  })),
  getSelectedObject: () => {
      const state = get();
      if (state.selectedIds.length !== 1) return null;
      return state.objects.find(o => o.id === state.selectedIds[0]) || null;
    },


  updateObject: (id, updates) => set((state) => ({
    history: [...state.history, state.objects],
    objects: state.objects.map((obj) =>
      obj.id === id
        ? {
            ...obj,
            ...updates,
            dataBinding: updates.dataBinding
              ? { ...updates.dataBinding }
              : obj.dataBinding
          }
        : obj
    ),
    isDirty: true,
    redoStack: []
  })),
  deleteSelected: () => set((state) => ({
    history: [...state.history, state.objects],
    objects: state.objects.filter(obj => !state.selectedIds.includes(obj.id)),
    selectedIds: [],
    redoStack: []
  })),

  // Undo / Redo
  undo: () => set((state) => {
    if (state.history.length === 0) return state;
    const previous = state.history[state.history.length - 1];
    if (isCardSettingSnapshot(previous)) {
      return {
        redoStack: [createCardSettingSnapshot(state), ...state.redoStack],
        cardSettings: { ...previous.cardSettings },
        surfaces: deepClone(previous.surfaces),
        activeSurface: previous.activeSurface,
        objects: deepClone(previous.objects),
        settingTransitionWarnings: deepClone(previous.settingTransitionWarnings),
        history: state.history.slice(0, -1),
        selectedIds: [],
        isDirty: true,
        stageKey: (Number(state.stageKey) || 0) + 1
      };
    }
    return {
      redoStack: [state.objects, ...state.redoStack],
      objects: previous,
      history: state.history.slice(0, -1),
      selectedIds: [],
    };
  }),
  redo: () => set((state) => {
    if (state.redoStack.length === 0) return state;
    const next = state.redoStack[0];
    if (isCardSettingSnapshot(next)) {
      return {
        history: [...state.history, createCardSettingSnapshot(state)],
        cardSettings: { ...next.cardSettings },
        surfaces: deepClone(next.surfaces),
        activeSurface: next.activeSurface,
        objects: deepClone(next.objects),
        settingTransitionWarnings: deepClone(next.settingTransitionWarnings),
        redoStack: state.redoStack.slice(1),
        selectedIds: [],
        isDirty: true,
        stageKey: (Number(state.stageKey) || 0) + 1
      };
    }
    return {
      history: [...state.history, state.objects],
      objects: next,
      redoStack: state.redoStack.slice(1),
      selectedIds: [],
    };
  }),

  // Layer control
  bringForward: () => set((state) => {
    if (state.selectedIds.length === 0) return {};

    const { bg, rest } = splitBackground(state.objects);
    let items = [...rest];

    // iterate from top → bottom to avoid double-swaps
    for (let i = items.length - 2; i >= 0; i--) {
      if (
        state.selectedIds.includes(items[i].id) &&
        !state.selectedIds.includes(items[i + 1].id)
      ) {
        [items[i], items[i + 1]] = [items[i + 1], items[i]];
      }
    }

    return {
      objects: bg ? [bg, ...items] : items
    };
  }),
  sendBackward: () => set((state) => {
    if (state.selectedIds.length === 0) return {};

    const { bg, rest } = splitBackground(state.objects);
    let items = [...rest];

    for (let i = 1; i < items.length; i++) {
      if (
        state.selectedIds.includes(items[i].id) &&
        !state.selectedIds.includes(items[i - 1].id)
      ) {
        [items[i - 1], items[i]] = [items[i], items[i - 1]];
      }
    }

    return {
      objects: bg ? [bg, ...items] : items
    };
  }),
  moveToTop: () => set((state) => {
    if (state.selectedIds.length === 0) return {};

    const { bg, rest } = splitBackground(state.objects);

    const selected = rest.filter(o => state.selectedIds.includes(o.id));
    const unselected = rest.filter(o => !state.selectedIds.includes(o.id));

    const reordered = [...unselected, ...selected];

    return {
      objects: bg ? [bg, ...reordered] : reordered
    };
  }),
  moveToBottom: () => set((state) => {
    if (state.selectedIds.length === 0) return {};

    const { bg, rest } = splitBackground(state.objects);

    const selected = rest.filter(o => state.selectedIds.includes(o.id));
    const unselected = rest.filter(o => !state.selectedIds.includes(o.id));

    const reordered = [...selected, ...unselected];

    return {
      objects: bg ? [bg, ...reordered] : reordered
    };
  }),

  // Clipboard
  // Clipboard
  copySelected: () => set((state) => {
    const copied = state.objects.filter(
      obj =>
        state.selectedIds.includes(obj.id) &&
        !isBackground(obj)
    );

    if (copied.length === 0) {
      return {}; // fail-safe: nothing copied
    }

    return {
      clipboard: copied.map(o => ({ ...o }))
    };
  }),

  cutSelected: () => set((state) => {
    const toCut = state.objects.filter(
      obj =>
        state.selectedIds.includes(obj.id) &&
        !isBackground(obj)
    );

    if (toCut.length === 0) return {};

    return {
      clipboard: toCut.map(o => ({ ...o })),
      objects: state.objects.filter(
        obj => !toCut.some(c => c.id === obj.id)
      ),
      selectedIds: [],
      isDirty: true
    };
  }),


  alignSelected: (alignment) => set((state) => {
    const objects = alignObjects(state.objects, state.selectedIds, alignment);
    if (objects === state.objects) return {};
    return {
      history: [...state.history, state.objects],
      objects,
      redoStack: [],
      isDirty: true
    };
  }),
  distributeSelectedHorizontal: () => set((state) => {
    const objects = distributeObjects(state.objects, state.selectedIds, 'horizontal');
    if (objects === state.objects) return {};
    return {
      history: [...state.history, state.objects],
      objects,
      redoStack: [],
      isDirty: true
    };
  }),
  distributeSelectedVertical: () => set((state) => {
    const objects = distributeObjects(state.objects, state.selectedIds, 'vertical');
    if (objects === state.objects) return {};
    return {
      history: [...state.history, state.objects],
      objects,
      redoStack: [],
      isDirty: true
    };
  }),
  rotateSelected90: () => set((state) => ({
    history: [...state.history, state.objects],
    objects: state.objects.map(obj =>
      state.selectedIds.includes(obj.id)
        ? {
            ...obj,
            rotation: ((obj.rotation || 0) + 90) % 360
          }
        : obj
    ),
    redoStack: []
  })),

  pasteClipboard: () => set((state) => {
    const newObjects = state.clipboard.map((obj, i) => ({
      ...obj,
      id: `${obj.id}_copy_${Date.now() + i}`,
      x: obj.x + 20 * (i + 1),
      y: obj.y + 20 * (i + 1)
    }));
    return {
      history: [...state.history, state.objects],
      objects: [...state.objects, ...newObjects],
      selectedIds: newObjects.map(o => o.id),
      redoStack: []
    };
  }),

  // Movement
  nudgeSelected: (dx, dy) => set((state) => ({
    history: [...state.history, state.objects],
    objects: state.objects.map((obj) =>
      state.selectedIds.includes(obj.id) ? { ...obj, x: obj.x + dx, y: obj.y + dy } : obj
    ),
    redoStack: []
  })),

  // Menu / Tool UI
  activeMenu: null,
  setActiveMenu: (menu) => set({ activeMenu: menu }),
  activeTool: null,
  setActiveTool: (tool) => set({ activeTool: tool }),

  scale: 1,
  setScale: (scale) => set({ scale }),

  isTransforming: false,
  setIsTransforming: (v) => set({ isTransforming: v }),

  setActiveSurface: (nextKey) =>
    set((state) => {
      const next = normalizeSurfaceKey(nextKey);

      // stash current working objects
      const updatedSurfaces = {
        ...state.surfaces,
        [state.activeSurface]: state.objects.map(o => ({ ...o })),
      };

      return {
        surfaces: updatedSurfaces,
        activeSurface: next,
        objects: deepClone(updatedSurfaces[next] || []),
        selectedIds: [],
        history: [],
        redoStack: [],
        stageKey: (Number(state.stageKey) || 0) + 1,
      };
    }),

    // Load card from backend
    loadCard: (card) => {
      if (!card.objectsFront && card.objects) {
        console.warn(
          'Legacy card format detected: card.objects used instead of objectsFront'
        );
      }
      const surfaces = {
        front: deepClone(card.objectsFront ?? card.objects ?? []),
        frontSilver: deepClone(card.objectsFrontSilver ?? []),
        back: deepClone(card.objectsBack ?? []),
        backSilver: deepClone(card.objectsBackSilver ?? []),
      };

      const activeSurface = normalizeSurfaceKey(card.activeSurface ?? 'front');

      set({
        cardId: card.cardId ?? card._id ?? null,
        capturePhotoBinding: card.capturePhotoBinding
          ? JSON.parse(JSON.stringify(card.capturePhotoBinding))
          : null,
        newCaptureInputDraft: card.prospectiveCaptureInputs === true && !card.cardId && !card._id,
        createdCaptureInputs: [],
        captureInputSelection: null,
        orgId: card.orgId ?? '',
        datasetId: card.datasetId ?? '',
        selectedTemplateId: card.templateId ?? null, // ✅ ADD THIS
        name: card.name ?? '',
        description: card.description ?? '',
        cardSettings: { ...card.cardSettings },
        surfaces,
        activeSurface,
        objects: deepClone(surfaces[activeSurface] || []),
        history: [],
        redoStack: [],
        selectedIds: [],
        isDirty: false,
        settingTransitionWarnings: [],
        stageKey: Date.now(),
      });
    },
    setIsDirty: (dirty = true) =>
      set(() => ({
        isDirty: dirty
      })),

    // Return card payload for saving
    getCardPayload: () => {
      const state = get();

      const bakedSurfaces = {
        ...state.surfaces,
        [state.activeSurface]: state.objects.map(o => stripRuntimeProps(o)),
      };



      const capturePhotoBinding = reconcileCaptureBinding(state, bakedSurfaces);
      return {
        ...(capturePhotoBinding ? {capturePhotoBinding} : {}),
        orgId: state.orgId,
        datasetId: state.datasetId,
        templateId: state.selectedTemplateId,
        name: state.name,
        description: state.description,
        cardSettings: state.cardSettings,

        objectsFront: bakedSurfaces.front.map(stripRuntimeProps),
        objectsFrontSilver: bakedSurfaces.frontSilver.map(stripRuntimeProps),
        objectsBack: bakedSurfaces.back.map(stripRuntimeProps),
        objectsBackSilver: bakedSurfaces.backSilver.map(stripRuntimeProps),
      };

    },

  resetCard: () =>
  set({
    cardId: null,
    capturePhotoBinding: null,
    newCaptureInputDraft: false,
    createdCaptureInputs: [],
    captureInputSelection: null,
    orgId: '',
    name: '',
    description: '',
    datasetId: '',
    cardSettings: {
      size: 'cr80',
      format: 'landscape',
      display: 'Side 1',
      sides: 'Simplex',
      silverFront: 'no',
      silverBack: 'no',
    },
    surfaces: getDefaultSurfaces(),
    activeSurface: 'front',
    objects: [],
    selectedIds: [],
    history: [],
    redoStack: [],
    settingTransitionWarnings: []
  }),

  updateCardSetting: (key, value) =>
    set((state) => {
      const prev = state.cardSettings;
      if (prev[key] === value) return {};

      // 🔥 STEP 1: Bake current working objects into surfaces
      const bakedSurfaces = {
        ...state.surfaces,
        [state.activeSurface]: state.objects.map(o => ({ ...o })),
      };

      const nextSettings = {
        ...prev,
        [key]: value,
      };

      const previousSnapshot = createCardSettingSnapshot(state, bakedSurfaces);
      const previousDimensions = getStageDimensions(prev);
      const nextDimensions = getStageDimensions(nextSettings);
      const destinationShrank = (key === 'size' || key === 'format') && (
        nextDimensions.cardWidth < previousDimensions.cardWidth ||
        nextDimensions.cardHeight < previousDimensions.cardHeight
      );
      const settingTransitionWarnings = [];
      const nextSurfaces = Object.fromEntries(SURFACE_KEYS.map(surface => {
        if (!destinationShrank) return [surface, bakedSurfaces[surface]];
        const result = fitObjectsWithinCard(
          bakedSurfaces[surface],
          nextDimensions.cardWidth,
          nextDimensions.cardHeight
        );
        settingTransitionWarnings.push(...result.warnings.map(warning => ({
          ...warning,
          surface
        })));
        return [surface, result.objects];
      }));
      let nextActiveSurface = state.activeSurface;

      // -----------------------------
      // 1️⃣ Duplex → Simplex (hide back surfaces without deleting them)
      // -----------------------------
      if (key === 'sides' && value === 'Simplex') {
        // If the active surface becomes hidden, move to the card front.
        if (state.activeSurface === 'back' || state.activeSurface === 'backSilver') {
          nextActiveSurface = 'front';
        }
      }

      // -----------------------------
      // 2️⃣ Silver Front OFF (preserve its objects while hidden)
      // -----------------------------
      if (key === 'silverFront' && value === 'no') {
        if (state.activeSurface === 'frontSilver') {
          nextActiveSurface = 'front';
        }
      }

      // -----------------------------
      // 3️⃣ Silver Back OFF (preserve its objects while hidden)
      // -----------------------------
      if (key === 'silverBack' && value === 'no') {
        if (state.activeSurface === 'backSilver') {
          nextActiveSurface = 'back';
        }
      }

      // 🔥 STEP 2: Load correct working surface
      const nextObjects = nextSurfaces[nextActiveSurface].map(o => ({ ...o }));

      return {
        cardSettings: nextSettings,
        surfaces: nextSurfaces,
        activeSurface: nextActiveSurface,
        objects: nextObjects,
        selectedIds: [],
        history: [...state.history, previousSnapshot],
        redoStack: [],
        settingTransitionWarnings,
        isDirty: true,
        stageKey: (Number(state.stageKey) || 0) + 1,
      };
    }),

assignImagePlaceholder: (objectId, placeholder) => {
  set((state) => ({
    objects: state.objects.map((obj) =>
      obj.id === objectId
        ? { ...obj, imagePlaceholder: placeholder }
        : obj
    )
  }));
},

// showPlaceholderModal: false,
// setShowPlaceholderModal: (value) => set({ showPlaceholderModal: value }),

previewSrc: '',
setPreviewSrc: (src) => set({ previewSrc: src }),

fitMode: 'fit',
setFitMode: (mode) => set({ fitMode: mode }),

bindingModal: {
  open: false,
  targetId: null
},

openBindingModal: (id) =>
  set({ bindingModal: { open: true, targetId: id } }),

closeBindingModal: () =>
  set({ bindingModal: { open: false, targetId: null } }),

  commitSelectedObjectsFromStage: () => {
    const state = get();
    const surface = state.activeSurface;

    const committed = state.objects.map(o => ({ ...o }));

    set({
      surfaces: {
        ...state.surfaces,
        [surface]: committed
      },
      objects: committed
    });
  },

  copySelectedToSurface: (targetSurface) => {
    // 1) Flush live Konva transforms
    const stateBefore = get();
    if (typeof stateBefore.flushTransforms === 'function') {
      stateBefore.flushTransforms();
    }

    const state = get();
    const target = targetSurface;

    if (!state.surfaces?.[target]) return;
    if (!state.selectedIds?.length) return;

    // 2) Bake current working objects into active surface
    const bakedSurfaces = {
      ...state.surfaces,
      [state.activeSurface]: state.objects.map(o => ({ ...o })),
    };

    const source = bakedSurfaces[state.activeSurface] || [];
    const selected = source.filter(o => state.selectedIds.includes(o.id));
    if (!selected.length) return;

    // 🔥 helper: convert to silver-safe object
    const toSilver = (obj) => {
      const copy = structuredClone(obj);

      // ❌ REMOVE ALL DATA BINDING
      delete copy.dataBinding;
      delete copy.textBinding;
      delete copy.fieldKey;
      delete copy.fieldId;
      delete copy.mapSlot;
      delete copy.mapLabel;

      // 🔥 PHOTO → RECT CONVERSION
      if (copy.type === 'photo') {
        return {
          id: generateId(),
          type: 'rect',

          x: copy.x,
          y: copy.y,
          width: copy.width,
          height: copy.height,
          rotation: copy.rotation || 0,

          cornerRadius: copy.cornerRadius || 0,

          fill: '#000000',
          stroke: null,
          strokeWidth: 0,

          opacity: copy.opacity ?? 1,

          draggable: copy.draggable,

          isSilver: true,
        };
      }

      // ✅ NON-PHOTO OBJECTS

      // Force fill to black
      if (copy.fill && copy.fill !== 'transparent') {
        copy.fill = '#000000';
      }

      // Force stroke to black
      if (copy.stroke && copy.stroke !== 'transparent') {
        copy.stroke = '#000000';
      }

      // Remove image-related props just in case
      delete copy.imagePlaceholder;
      delete copy.src;
      delete copy.originalSrc;

      copy.isSilver = true;

      return copy;
    };

    // 3) Create copies
    const copies = selected.map(o => ({
      ...toSilver(o),
      id: generateId(),
    }));

    // 4) Append to target surface
    set({
      surfaces: {
        ...bakedSurfaces,
        [target]: [...(bakedSurfaces[target] || []), ...copies],
      },
    });
  },

}));

export default useDesignerStore;
